import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';

const StudentEnrollment = () => {
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [bulkEnrollmentForm, setBulkEnrollmentForm] = useState({
  class_id: '',
  session_id: '',
  term_id: ''
});
  
  const [filters, setFilters] = useState({
    class_id: '',
    session_id: '',
    term_id: '',
    status: ''
  });

  // Single enrollment form state
  const [singleEnrollmentData, setSingleEnrollmentData] = useState({
    student_reg: '',
    class_id: '',
    academic_session_id: '',
    term_id: ''
  });

  // Validation schema for single enrollment
  const singleEnrollmentSchema = Yup.object().shape({
    student_reg: Yup.string().required('Student registration number is required'),
    class_id: Yup.number().required('Class selection is required'),
    academic_session_id: Yup.number().required('Academic session selection is required'),
    term_id: Yup.number().required('Term selection is required')
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classesRes, sessionsRes, studentsRes] = await Promise.all([
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions(),
        adminAPI.getEnrolledStudents(),
      ]);
      
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
      setStudents(studentsRes.data);
      
      // Extract terms from sessions
      const allTerms = [];
      sessionsRes.data.forEach(session => {
        if (session.terms) {
          session.terms.forEach(term => {
            allTerms.push({
              ...term,
              session_name: session.academic_session,
              academic_session_id: session.id
            });
          });
        }
      });
      setTerms(allTerms);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle single enrollment
  const handleSingleEnrollment = async (values, { setSubmitting, resetForm }) => {
    try {
      setEnrollmentLoading(true);
      await adminAPI.enrollStudent(values);
      toast.success('Student enrolled successfully');
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrollmentLoading(false);
      setSubmitting(false);
    }
  };

  // Handle bulk enrollment from Excel
  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      setExcelData(jsonData);
      
      // Extract student registration numbers
      const regNumbers = jsonData.map(row => row['Student Registration'] || row['Registration'] || row['ID']);
      setSelectedStudents(regNumbers.filter(Boolean));
    };
    reader.readAsBinaryString(file);
  };

  // const handleBulkEnroll = async (values) => {
  //   try {
  //     const enrollmentData = {
  //       student_regs: selectedStudents,
  //       class_id: values.class_id,
  //       academic_session_id: values.session_id,
  //       term_id: values.term_id
  //     };

  //     const response = await adminAPI.bulkEnrollStudents(enrollmentData);
  //     toast.success(response.data.message);
      
  //     if (response.data.failed && response.data.failed.length > 0) {
  //       console.warn('Failed enrollments:', response.data.failed);
  //       // You could show these errors to the user
  //     }
      
  //     setShowBulkModal(false);
  //     fetchData();
  //   } catch (error) {
  //     toast.error('Failed to enroll students');
  //   }
  // };

  // Download enrollment template
  const handleDownloadEnrollmentTemplate = () => {
    // Create sample data
    const templateData = [
      ['Student Registration'],
      ['STU2024001'],
      ['STU2024002'],
      ['STU2024003'],
      ['STU2024004']
    ];
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Add template sheet
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    
    // Add instructions sheet
    const instructions = [
      ['Column Name', 'Description', 'Required', 'Example', 'How to Get Value'],
      ['Student Registration', 'Existing student registration number', 'Yes', 'STU2024001', 'Copy from Student Management'],
      ['Class ID', 'Numeric class ID from system', 'Yes', '1', 'Get ID from Classes page'],
      ['Academic Session ID', 'Numeric session ID from system', 'Yes', '1', 'Get ID from Academic Sessions page'],
      ['Term ID', 'Numeric term ID from system', 'Yes', '1', 'Get ID from Academic Sessions page'],
      ['', '', '', '', ''],
      ['Important Instructions:', '', '', '', ''],
      ['1. DO NOT change the column headers in Row 1', '', '', '', ''],
      ['2. Use exact IDs from the system (not names)', '', '', '', ''],
      ['3. Remove example rows (2-5) before uploading', '', '', '', ''],
      ['4. Save file as .xlsx format for best results', '', '', '', ''],
      ['5. Maximum 1000 rows per file', '', '', '', ''],
      ['', '', '', '', ''],
      ['ID Reference Table:', '', '', '', ''],
      ['Type', 'Name Example', 'ID Example', '', ''],
      ['Class', 'Primary 1A', '1', '', ''],
      ['Session', '2023/2024', '1', '', ''],
      ['Term', 'First Term', '1', '', '']
    ];
    
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    // Set column widths for instructions
    const colWidths = [
      { wch: 20 }, // Column Name
      { wch: 35 }, // Description
      { wch: 10 }, // Required
      { wch: 15 }, // Example
      { wch: 30 }  // How to Get Value
    ];
    ws2['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(wb, ws2, 'Instructions');
    
    // Add reference data sheet
    const referenceData = [];
    referenceData.push(['Class Reference:', '', '', '']);
    referenceData.push(['ID', 'Class Name']);
    classes.forEach(cls => {
      referenceData.push([cls.id, cls.class_name]);
    });
    
    referenceData.push(['', '', '', '']);
    referenceData.push(['Session Reference:', '', '', '']);
    referenceData.push(['ID', 'Academic Session']);
    sessions.forEach(session => {
      referenceData.push([session.id, session.academic_session]);
    });
    
    referenceData.push(['', '', '', '']);
    referenceData.push(['Term Reference:', '', '', '']);
    referenceData.push(['ID', 'Term', 'Session ID', 'Session Name']);
    terms.forEach(term => {
      referenceData.push([term.id, term.term, term.academic_session_id, term.session_name]);
    });
    
    const ws3 = XLSX.utils.aoa_to_sheet(referenceData);
    XLSX.utils.book_append_sheet(wb, ws3, 'ID Reference');
    
    // Download the file
    XLSX.writeFile(wb, 'enrollment_template.xlsx');
    toast.success('Template downloaded successfully');
  };

  // Filter students based on selected filters
  const filteredStudents = students.filter(student => {
    const matchesClass = !filters.class_id || 
      student.class_id == filters.class_id;
    
    const matchesSession = !filters.session_id || 
      student.academic_session_id == filters.session_id;
    
    const matchesTerm = !filters.term_id || 
      student.term_id == filters.term_id;
    
    const matchesStatus = !filters.status || 
      student.application_status == filters.status;
    
    return matchesClass && matchesSession && matchesTerm && matchesStatus;
  });




  const handleBulkEnroll = async () => {
  try {
    // Validate that all required fields are filled
    if (!bulkEnrollmentForm.class_id || !bulkEnrollmentForm.session_id || !bulkEnrollmentForm.term_id) {
      toast.error('Please select class, session, and term before enrolling');
      return;
    }

    const enrollmentData = {
      student_regs: selectedStudents,
      class_id: bulkEnrollmentForm.class_id,
      academic_session_id: bulkEnrollmentForm.session_id, // Changed from session_id to academic_session_id
      term_id: bulkEnrollmentForm.term_id
    };

    const response = await adminAPI.bulkEnrollStudents(enrollmentData);
    toast.success(response.data.message);
    
    if (response.data.failed && response.data.failed.length > 0) {
      console.warn('Failed enrollments:', response.data.failed);
      // Show errors to the user
      toast.error(`${response.data.failed.length} students failed to enroll. Check console for details.`);
    }
    
    setShowBulkModal(false);
    fetchData();
    
    // Reset bulk form after successful enrollment
    setBulkEnrollmentForm({
      class_id: '',
      session_id: '',
      term_id: ''
    });
    setExcelData([]);
    setSelectedStudents([]);
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Failed to enroll students');
  }
};



  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Enrollment</h1>
          <p className="text-gray-600">Enroll students into classes for specific academic sessions and terms</p>
        </div>
        <div className="space-x-2">
          <button
            onClick={handleDownloadEnrollmentTemplate}
            className="btn-secondary flex items-center"
          >
            <i className="fas fa-download mr-2"></i> Download Template
          </button>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="bg-white rounded-lg shadow">
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('single')}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'single'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-user-plus mr-2"></i>
              Single Enrollment
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'bulk'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-file-import mr-2"></i>
              Bulk Enrollment
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Single Enrollment Tab */}
          {activeTab === 'single' && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-blue-500 mt-0.5 mr-3"></i>
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Single Enrollment Instructions</h4>
                    <p className="text-sm text-blue-700">
                      Enroll a single student by selecting their registration number, class, academic session, and term.
                      All fields are required.
                    </p>
                  </div>
                </div>
              </div>

              {/* Single Enrollment Form */}
              <Formik
                initialValues={singleEnrollmentData}
                validationSchema={singleEnrollmentSchema}
                onSubmit={handleSingleEnrollment}
              >
                {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Student Registration */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Student Registration Number *
                        </label>
                        <Field
                          type="text"
                          name="student_reg"
                          className="input-field"
                          placeholder="e.g., STU2024001"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter the exact registration number of the student
                        </p>
                        {errors.student_reg && touched.student_reg && (
                          <div className="text-red-500 text-sm mt-1">{errors.student_reg}</div>
                        )}
                      </div>

                      {/* Class Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Class *
                        </label>
                        <Field as="select" name="class_id" className="input-field">
                          <option value="">Select Class</option>
                          {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </Field>
                        {errors.class_id && touched.class_id && (
                          <div className="text-red-500 text-sm mt-1">{errors.class_id}</div>
                        )}
                      </div>

                      {/* Academic Session Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Academic Session *
                        </label>
                        <Field as="select" name="academic_session_id" className="input-field">
                          <option value="">Select Session</option>
                          {sessions.map(session => (
                            <option key={session.id} value={session.id}>
                              {session.academic_session}
                            </option>
                          ))}
                        </Field>
                        {errors.academic_session_id && touched.academic_session_id && (
                          <div className="text-red-500 text-sm mt-1">{errors.academic_session_id}</div>
                        )}
                      </div>

                      {/* Term Selection (filtered by session) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Term *
                        </label>
                        <Field as="select" name="term_id" className="input-field">
                          <option value="">Select Term</option>
                          {terms
                            .filter(term => !values.academic_session_id || 
                                    term.academic_session_id == values.academic_session_id)
                            .map(term => (
                              <option key={term.id} value={term.id}>
                                {term.term} ({term.session_name})
                              </option>
                            ))
                          }
                        </Field>
                        {errors.term_id && touched.term_id && (
                          <div className="text-red-500 text-sm mt-1">{errors.term_id}</div>
                        )}
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary px-6 py-2"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Enrolling...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-user-plus mr-2"></i>
                            Enroll Student
                          </>
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {/* Bulk Enrollment Tab */}
          {activeTab === 'bulk' && (
            <div className="space-y-6">
              {/* Bulk Enrollment Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-blue-500 mt-0.5 mr-3"></i>
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-800 mb-2">Excel Template Pattern for Bulk Enrollment</h4>
                    
                    <div className="overflow-x-auto mb-3">
                      <table className="min-w-full border border-gray-300 bg-white">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border px-3 py-2 text-left text-sm font-medium text-gray-700">Student Registration</th>
                            {/* <th className="border px-3 py-2 text-left text-sm font-medium text-gray-700">Class ID</th>
                            <th className="border px-3 py-2 text-left text-sm font-medium text-gray-700">Academic Session ID</th>
                            <th className="border px-3 py-2 text-left text-sm font-medium text-gray-700">Term ID</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border px-3 py-2 text-sm">STU2024001</td>
                            {/* <td className="border px-3 py-2 text-sm">1</td>
                            <td className="border px-3 py-2 text-sm">1</td>
                            <td className="border px-3 py-2 text-sm">1</td> */}
                          </tr>
                          <tr>
                            <td className="border px-3 py-2 text-sm">STU2024002</td>
                            {/* <td className="border px-3 py-2 text-sm">1</td>
                            <td className="border px-3 py-2 text-sm">1</td>
                            <td className="border px-3 py-2 text-sm">2</td> */}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="text-sm text-blue-700 space-y-2">
                      <p><strong>Important Notes:</strong></p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Download the template above for correct format</li>
                        <li>IDs must match existing records in the system</li>
                        <li>Use the ID Reference sheet in the template for correct IDs</li>
                        <li>Do not modify column headers</li>
                        <li>Maximum 1000 rows per file</li>
                        <li>Save as .xlsx or .csv format</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bulk Upload Section */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mb-4">
                    <i className="fas fa-file-excel text-primary-600 text-xl"></i>
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Excel File</h3>
                  <p className="text-gray-500 mb-6">Upload your Excel file with student enrollment data</p>
                  
                  <div className="max-w-md mx-auto">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleExcelUpload}
                      className="hidden"
                      id="bulk-file-upload"
                    />
                    <label
                      htmlFor="bulk-file-upload"
                      className="block w-full cursor-pointer border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-lg p-8 text-center transition-colors"
                    >
                      <div className="flex flex-col items-center">
                        <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-3"></i>
                        <span className="text-primary-600 font-medium">
                          Click to upload Excel file
                        </span>
                        <span className="text-sm text-gray-500 mt-1">
                          .xlsx, .xls, or .csv files only
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {excelData.length > 0 && (
                  <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-800">
                        <i className="fas fa-check-circle text-green-500 mr-2"></i>
                        File Uploaded Successfully
                      </h4>
                      <span className="text-sm text-gray-500">
                        {excelData.length} rows found
                      </span>
                    </div>
                    
                    <div className="text-sm">
                      <p className="text-gray-600 mb-2">Sample of uploaded data:</p>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              {Object.keys(excelData[0]).map((key) => (
                                <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                  {key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {excelData.slice(0, 5).map((row, index) => (
                              <tr key={index}>
                                {Object.values(row).map((value, idx) => (
                                  <td key={idx} className="px-3 py-2 text-sm">
                                    {value}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview and Actions */}
              {selectedStudents.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-800">Ready to Enroll</h4>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      {selectedStudents.length} students
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Class *
                        </label>
                        <select className="input-field">
                          <option value="">Choose class</option>
                          {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Session *
                        </label>
                        <select className="input-field">
                          <option value="">Choose session</option>
                          {sessions.map(session => (
                            <option key={session.id} value={session.id}>
                              {session.academic_session}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Term *
                        </label>
                        <select className="input-field">
                          <option value="">Choose term</option>
                          {terms.map(term => (
                            <option key={term.id} value={term.id}>
                              {term.term}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        className="btn-primary px-6 py-2"
                        onClick={() => setShowBulkModal(true)}
                      >
                        <i className="fas fa-user-plus mr-2"></i>
                        Proceed to Enroll
                      </button>
                    </div> */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Select Class *
    </label>
    <select 
      className="input-field"
      value={bulkEnrollmentForm.class_id}
      onChange={(e) => setBulkEnrollmentForm({...bulkEnrollmentForm, class_id: e.target.value})}
    >
      <option value="">Choose class</option>
      {classes.map(cls => (
        <option key={cls.id} value={cls.id}>
          {cls.class_name}
        </option>
      ))}
    </select>
  </div>

  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Select Session *
    </label>
    <select 
      className="input-field"
      value={bulkEnrollmentForm.session_id}
      onChange={(e) => setBulkEnrollmentForm({...bulkEnrollmentForm, session_id: e.target.value})}
    >
      <option value="">Choose session</option>
      {sessions.map(session => (
        <option key={session.id} value={session.id}>
          {session.academic_session}
        </option>
      ))}
    </select>
  </div>

  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Select Term *
    </label>
    <select 
      className="input-field"
      value={bulkEnrollmentForm.term_id}
      onChange={(e) => setBulkEnrollmentForm({...bulkEnrollmentForm, term_id: e.target.value})}
    >
      <option value="">Choose term</option>
      {terms
        .filter(term => !bulkEnrollmentForm.session_id || term.academic_session_id == bulkEnrollmentForm.session_id)
        .map(term => (
          <option key={term.id} value={term.id}>
            {term.term} ({term.session_name})
          </option>
        ))
      }
    </select>
  </div>
</div>

{/* // Update the modal's enroll button to call handleBulkEnroll without parameters */}
<button
  onClick={handleBulkEnroll}
  className="btn-primary px-4 py-2"
>
  <i className="fas fa-user-plus mr-2"></i>
  Enroll All Students
</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enrollment List Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-800">Enrolled Students</h2>
              <p className="text-sm text-gray-600">
                Total: {filteredStudents.length} students
              </p>
            </div>
            
            {/* Filters */}
            <div className="flex space-x-3">
              <select 
                className="input-field text-sm"
                value={filters.class_id}
                onChange={(e) => setFilters({...filters, class_id: e.target.value})}
              >
                <option value="">All Classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.class_name}</option>
                ))}
              </select>
              
              <select 
                className="input-field text-sm"
                value={filters.session_id}
                onChange={(e) => setFilters({...filters, session_id: e.target.value})}
              >
                <option value="">All Sessions</option>
                {sessions.map(session => (
                  <option key={session.id} value={session.id}>{session.academic_session}</option>
                ))}
              </select>
              
              <select 
                className="input-field text-sm"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session/Term
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student.student_reg} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-user-graduate text-green-600"></i>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.student_reg}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {student.class_name || 'Not assigned'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {student.academic_session || 'No session'}
                      </div>
                      <div className="text-gray-500">
                        Term: {student.term || 'No term'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      student.application_status === 'approved' 
                        ? 'bg-green-100 text-green-800'
                        : student.application_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {student.application_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        // Edit enrollment
                        setActiveTab('single');
                        setSingleEnrollmentData({
                          student_reg: student.student_reg,
                          class_id: student.class_id || '',
                          academic_session_id: student.academic_session_id || '',
                          term_id: student.term_id || ''
                        });
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <i className="fas fa-edit mr-1"></i> Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to remove this enrollment?')) {
                          try {
                            // You would need to implement an unenroll endpoint
                            // await adminAPI.unenrollStudent(student.student_reg);
                            toast.success('Student enrollment removed');
                            fetchData();
                          } catch (error) {
                            toast.error('Failed to remove enrollment');
                          }
                        }
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="fas fa-trash mr-1"></i> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-users text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No enrolled students found</p>
            <p className="text-sm text-gray-400 mt-1">
              Use the enrollment forms above to add students
            </p>
          </div>
        )}
      </div>

      {/* Bulk Enrollment Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title="Confirm Bulk Enrollment"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <i className="fas fa-exclamation-triangle text-yellow-500 mt-0.5 mr-3"></i>
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Are you sure?</h4>
                <p className="text-sm text-yellow-700">
                  You are about to enroll {selectedStudents.length} students. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Students to enroll:</span>
              <span className="font-medium">{selectedStudents.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">File name:</span>
              <span className="font-medium">enrollment_data.xlsx</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Uploaded at:</span>
              <span className="font-medium">{new Date().toLocaleTimeString()}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowBulkModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkEnroll}
              className="btn-primary px-4 py-2"
            >
              <i className="fas fa-user-plus mr-2"></i>
              Enroll All Students
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentEnrollment;